/**
 * `/24` + ASN adjacency to already-classified case IPs (v1.147.0).
 *
 * The problem this solves. Every per-IP verdict the portal computes evaluates an
 * address *in isolation* — reputation, geo, device binding. Attacker infrastructure
 * does not arrive in isolation: it clusters. On `IR-20260818-Controller-001` the
 * analyst had already classified `216.203.13.27` and `.69` as malicious, and the two
 * IPs the case could not see at all — `216.203.13.24` and `.26` — sit in the same
 * `/24` on the same ASN. A badge reading "2 other IPs in this /24 are classified
 * malicious" promotes both with no new enrichment, no new feed, and no judgement call.
 *
 * It is **self-bootstrapping**: adjacency is computed against the case's own
 * `ipClassifications`, so the first malicious classification lights up its neighbours,
 * and the signal strengthens as the analyst works.
 *
 * ASN comes from the NON-INTERACTIVE FEED's own `$.autonomousSystemNumber`, not from
 * IP enrichment. That matters: `216.203.13.24` and `.26` have no enrichment record at
 * all, yet the feed reports ASN 136787 for both — the same PacketHub ASN as the
 * already-classified `.27`. Keying adjacency on enrichment would have been blind in
 * exactly the place the analyst needed it.
 *
 * ⚠️ Honest limit: adjacency alone would NOT have closed the reference gap.
 * `43.166.208.95` reports ASN `0` and has no classified neighbour in its `/24`; it is
 * carried by the device rule instead. Adjacency pays off *after* the aggregate query
 * puts the addresses on the table, which is why the two ship together.
 *
 * IPv4 `/24` only, deliberately. The user's own IPv6 addresses on the reference case
 * span many distinct `/64`s (`2607:fb91:169e:…`, `2607:fb91:1614:…`,
 * `2605:59ca:690f:…`), so a `/64` rule would essentially never fire, and a shorter
 * prefix would group unrelated consumer allocations into one bucket. ASN adjacency
 * covers both families.
 *
 * Kept free of React so `frontend/scripts/ipAdjacency.test.mjs` can exercise it.
 */

const str = (v) => (v === null || v === undefined ? '' : String(v));

/** Strip a trailing port and IPv6 brackets, matching the server's canonical form. */
export function cleanIp(ip) {
  let s = str(ip).trim();
  if (!s) return '';
  const bracketed = s.match(/^\[([^\]]+)\](?::\d+)?$/);
  if (bracketed) return bracketed[1].toLowerCase();
  const v4WithPort = s.match(/^(\d{1,3}(?:\.\d{1,3}){3}):\d+$/);
  if (v4WithPort) return v4WithPort[1];
  return s.toLowerCase();
}

/**
 * The `/24` key for an IPv4 address (`"216.203.13."`), or null.
 *
 * Returns null for IPv6 and for anything that is not four octets in 0-255 — a
 * malformed value must not silently share a bucket with real addresses.
 */
export function ipv4Prefix24(ip) {
  const s = cleanIp(ip);
  const m = s.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!m) return null;
  for (let i = 1; i <= 4; i += 1) {
    const n = Number(m[i]);
    if (!Number.isInteger(n) || n < 0 || n > 255) return null;
  }
  return `${m[1]}.${m[2]}.${m[3]}.`;
}

/** An ASN is usable only when it is a positive integer; `0` means "unknown", not AS0. */
export function normalizeAsn(asn) {
  const s = str(asn).trim().replace(/^AS/i, '');
  if (!/^\d+$/.test(s)) return '';
  const n = Number(s);
  return n > 0 ? String(n) : '';
}

/**
 * Index the case's classified IPs by `/24` and by ASN.
 *
 * @param ipClassifications {Object} `{ ip: 'safe'|'suspect'|'malicious' }` — the case's own map.
 * @param asnByIp {Object} `{ ip: asn }` — from the non-interactive aggregate; may be empty.
 */
export function buildAdjacencyIndex(ipClassifications = {}, asnByIp = {}) {
  const byPrefix = new Map();   // "a.b.c." -> { malicious: [ip], suspect: [ip], safe: [ip] }
  const byAsn = new Map();      // "136787" -> same shape
  const asnFor = new Map();     // cleaned ip -> asn

  Object.entries(asnByIp || {}).forEach(([ip, asn]) => {
    const a = normalizeAsn(asn);
    if (a) asnFor.set(cleanIp(ip), a);
  });

  const bucket = (map, key) => {
    if (!map.has(key)) map.set(key, { malicious: [], suspect: [], safe: [] });
    return map.get(key);
  };

  Object.entries(ipClassifications || {}).forEach(([rawIp, rawStatus]) => {
    const status = str(rawStatus).toLowerCase();
    if (status !== 'malicious' && status !== 'suspect' && status !== 'safe') return;
    const ip = cleanIp(rawIp);
    if (!ip) return;

    const prefix = ipv4Prefix24(ip);
    if (prefix) bucket(byPrefix, prefix)[status].push(ip);

    const asn = asnFor.get(ip);
    if (asn) bucket(byAsn, asn)[status].push(ip);
  });

  return { byPrefix, byAsn, asnFor };
}

/**
 * Adjacency for one IP. Counts EXCLUDE the IP itself, so an address cannot be its own
 * neighbour — without that, classifying an IP malicious would immediately claim it has
 * a malicious neighbour, which is circular and would fire on every classified row.
 */
export function adjacencyFor(ip, index) {
  const empty = {
    ip: '', prefix: null, asn: '',
    maliciousInPrefix: 0, suspectInPrefix: 0, safeInPrefix: 0, prefixPeers: [],
    maliciousInAsn: 0, suspectInAsn: 0, safeInAsn: 0, asnPeers: [],
    hasSignal: false,
  };
  if (!index) return empty;

  const clean = cleanIp(ip);
  if (!clean) return empty;

  const prefix = ipv4Prefix24(clean);
  const asn = index.asnFor?.get(clean) || '';

  const others = (list) => (list || []).filter((peer) => peer !== clean);

  const p = (prefix && index.byPrefix?.get(prefix)) || null;
  const a = (asn && index.byAsn?.get(asn)) || null;

  const prefixMal = others(p?.malicious);
  const prefixSus = others(p?.suspect);
  const prefixSafe = others(p?.safe);
  const asnMal = others(a?.malicious);
  const asnSus = others(a?.suspect);
  const asnSafe = others(a?.safe);

  return {
    ip: clean,
    prefix: prefix ? `${prefix}0/24` : null,
    asn,
    maliciousInPrefix: prefixMal.length,
    suspectInPrefix: prefixSus.length,
    safeInPrefix: prefixSafe.length,
    prefixPeers: prefixMal.concat(prefixSus),
    maliciousInAsn: asnMal.length,
    suspectInAsn: asnSus.length,
    safeInAsn: asnSafe.length,
    asnPeers: asnMal.concat(asnSus),
    // Only malicious/suspect neighbours are a REASON TO LOOK. A safe-neighbour count is
    // shown inside the expanded panel (it helps clear an address) but must not raise a
    // badge on the row — an analyst should not be nudged toward "safe" by proximity.
    hasSignal: prefixMal.length > 0 || asnMal.length > 0 || prefixSus.length > 0 || asnSus.length > 0,
  };
}

/** One-line badge text, or '' when there is nothing worth saying. */
export function adjacencyBadgeText(adj) {
  if (!adj || !adj.hasSignal) return '';
  const parts = [];
  if (adj.maliciousInPrefix > 0) {
    parts.push(`${adj.maliciousInPrefix} malicious in ${adj.prefix}`);
  } else if (adj.suspectInPrefix > 0) {
    parts.push(`${adj.suspectInPrefix} suspect in ${adj.prefix}`);
  }
  if (adj.maliciousInAsn > 0 && adj.maliciousInPrefix === 0) {
    parts.push(`${adj.maliciousInAsn} malicious on AS${adj.asn}`);
  }
  return parts.join(' · ');
}
